import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";

import "./xero-organisations-panel.scss";

/**
 * Master Admin → Xero Organisations.
 *
 * Connect one or more Xero organisations (UK / Europe / UAE …) via the standard
 * OAuth "Connect to Xero" flow. Connected orgs appear in the PAYMENTS-section
 * dropdown on the order and sell flows, where the rep picks which org an
 * invoice / purchase order is created in.
 *
 * Endpoints (backend retail-circle.com):
 *   GET    /api/xero/connections          → { configured, connections }
 *   POST   /api/xero/connections/connect  → { url }  (open to start OAuth)
 *   PATCH  /api/xero/connections/{id}      label / currency / default / enabled
 *   DELETE /api/xero/connections/{id}      disconnect
 */
export function XeroOrganisationsPanel() {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const [configured, setConfigured] = useState(true);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/xero/connections");
      setConfigured(data?.configured !== false);
      setConnections(data?.connections || []);
    } catch {
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Surface the OAuth round-trip result (?xero=connected|denied|error|…).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("xero");
    if (!status) return;
    if (status === "connected") {
      alert.show("Xero organisation connected.");
    } else if (status !== null) {
      alert.show(`Xero connection ${status.replace(/_/g, " ")}.`);
    }
    // Clean the query string so the message doesn't reappear on reload.
    params.delete("xero");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "")
    );
  }, [alert]);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.post("/api/xero/connections/connect");
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      alert.show("Could not start the Xero connection.");
    } catch (err) {
      showApiError(err, "start the Xero connection");
    } finally {
      setConnecting(false);
    }
  };

  const patch = async (id, payload) => {
    try {
      await axios.patch(`/api/xero/connections/${id}`, payload);
      await load();
    } catch (err) {
      showApiError(err, "update the organisation");
    }
  };

  const onDisconnect = async (conn) => {
    if (
      !window.confirm(
        `Disconnect "${conn.org_label}"? It will no longer be selectable for new invoices/purchase orders.`
      )
    )
      return;
    try {
      await axios.delete(`/api/xero/connections/${conn.id}`);
      await load();
    } catch (err) {
      showApiError(err, "disconnect Xero");
    }
  };

  return (
    <div className="xero-orgs">
      <p className="xero-orgs__intro">
        Connect the Xero organisations you invoice through. Connected orgs appear
        in the PAYMENTS-section dropdown on the order and sell flows.
      </p>

      {!configured && (
        <div className="xero-orgs__warning">
          Xero OAuth isn’t configured on this environment yet
          (<code>XERO_OAUTH_CLIENT_ID</code> / <code>XERO_OAUTH_CLIENT_SECRET</code>{" "}
          / <code>XERO_OAUTH_REDIRECT_URI</code>). You can still see the existing
          organisation, but “Connect to Xero” won’t work until it’s set.
        </div>
      )}

      {loading ? (
        <div className="xero-orgs__empty">Loading…</div>
      ) : connections.length === 0 ? (
        <div className="xero-orgs__empty">No organisations connected yet.</div>
      ) : (
        <div className="xero-orgs__list">
          {connections.map((c) => (
            <div className="xero-orgs__card" key={c.id}>
              <div className="xero-orgs__row">
                <input
                  className="xero-orgs__label"
                  defaultValue={c.org_label}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== c.org_label) patch(c.id, { org_label: v });
                  }}
                />

                {/* Base currency is the org's own (read from Xero) — the
                    currency invoices/POs are issued in. Not editable: it must
                    match the Xero org or invoices would be rejected. */}
                <span
                  className="xero-orgs__currency"
                  title="Base currency of this Xero organisation — invoices and purchase orders are issued in it. Read from Xero."
                >
                  {(c.currency || "").toUpperCase() || "—"}
                  <span className="xero-orgs__currency-note">base currency</span>
                </span>

                <span
                  className={`xero-orgs__status xero-orgs__status--${
                    c.connected ? "ok" : "off"
                  }`}
                >
                  {c.connected ? "Connected" : "Not connected"}
                </span>
              </div>

              <div className="xero-orgs__row xero-orgs__row--controls">
                <label className="xero-orgs__check">
                  <input
                    type="radio"
                    name="xero-default"
                    checked={!!c.is_default}
                    onChange={() => patch(c.id, { is_default: true })}
                  />
                  Default
                </label>

                <label className="xero-orgs__check">
                  <input
                    type="checkbox"
                    checked={!!c.enabled}
                    onChange={(e) => patch(c.id, { enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                {c.tenant_name && (
                  <span className="xero-orgs__tenant">{c.tenant_name}</span>
                )}

                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger xero-orgs__disconnect"
                  onClick={() => onDisconnect(c)}
                >
                  {c.auth_mode === "custom_connection" ? "Remove" : "Disconnect"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="xero-orgs__actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onConnect}
          disabled={connecting || !configured}
        >
          {connecting ? "Starting…" : "Connect to Xero"}
        </button>
      </div>
    </div>
  );
}
